import { supabase } from '../utils/supabaseClient';
import { offlineSupabase } from '../utils/offlineSupabaseProxy';

export interface UserProfile {
  id: string;
  full_name?: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  role: 'initial' | 'user' | 'disapproved' | 'admin';
  created_at: string;
  updated_at: string;
}

export interface UserManagementFilters {
  role?: 'initial' | 'user' | 'disapproved' | 'admin';
  search?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface UserManagementResponse {
  data: UserProfile[];
  count: number;
  error: string | null;
}

export class UserManagementService {
  // Get all users with filtering and pagination
  static async getUsers(
    filters: UserManagementFilters = {},
    pagination: PaginationParams = {}
  ): Promise<UserManagementResponse> {
    try {
      const { page = 1, limit = 10 } = pagination;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      // supabase client is already configured with altzero schema
      let query = supabase
        .from('base_profiles')
        .select('*', { count: 'exact' });

      if (filters.role) {
        query = query.eq('role', filters.role);
      }

      if (filters.search) {
        query = query.or(
          `full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
        );
      }

      const { data, error, count } = await query
        .range(from, to)
        .order('created_at', { ascending: false });

      console.log('[UserManagement] getUsers result:', { data: data?.length, count, error });

      if (error) {
        return {
          data: [],
          count: 0,
          error: error.message || JSON.stringify(error),
        };
      }

      return {
        data: data || [],
        count: count || 0,
        error: null,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        data: [],
        count: 0,
        error: error instanceof Error ? error.message : JSON.stringify(error),
      };
    }
  }

  // Get user statistics
  static async getUserStats(): Promise<{
    data: {
      total: number;
      initial: number;
      approved: number;
      disapproved: number;
      admin: number;
    } | null;
    error: string | null;
  }> {
    try {
      const [totalRes, initialRes, approvedRes, disapprovedRes, adminRes] =
        await Promise.all([
          supabase.from('base_profiles').select('*', { count: 'exact', head: true }),
          supabase.from('base_profiles').select('*', { count: 'exact', head: true }).eq('role', 'initial'),
          supabase.from('base_profiles').select('*', { count: 'exact', head: true }).eq('role', 'user'),
          supabase.from('base_profiles').select('*', { count: 'exact', head: true }).eq('role', 'disapproved'),
          supabase.from('base_profiles').select('*', { count: 'exact', head: true }).eq('role', 'admin'),
        ]);

      return {
        data: {
          total: totalRes.count || 0,
          initial: initialRes.count || 0,
          approved: approvedRes.count || 0,
          disapproved: disapprovedRes.count || 0,
          admin: adminRes.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch user statistics',
      };
    }
  }

  // Approve a user (change role from initial/disapproved to user)
  static async approveUser(
    userId: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('base_profiles')
        .update({ role: 'user', updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error approving user:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to approve user',
      };
    }
  }

  // Disapprove a user (change role to disapproved)
  static async disapproveUser(
    userId: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('base_profiles')
        .update({ role: 'disapproved', updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error disapproving user:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to disapprove user',
      };
    }
  }

  // Bulk approve multiple users
  static async bulkApproveUsers(
    userIds: string[]
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await offlineSupabase
        .schema('altzero')
        .from('base_profiles')
        .update({
          role: 'user',
          updated_at: new Date().toISOString(),
        })
        .in('id', userIds);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error bulk approving users:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to approve users',
      };
    }
  }

  // Get pending users count (for notifications)
  static async getPendingUsersCount(): Promise<{
    count: number;
    error: string | null;
  }> {
    try {
      const { count, error } = await offlineSupabase
        .schema('altzero')
        .from('base_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'initial');

      if (error) {
        throw error;
      }

      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error fetching pending users count:', error);
      return {
        count: 0,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch pending users count',
      };
    }
  }

  // Check if user signup blocking is bypassed
  static isSignupBlockBypassed(): boolean {
    return import.meta.env.VITE_BYPASS_SIGNUP_BLOCK === 'true';
  }
}
